/**
 * Nízkoúrovňové operácie nad rozpracovaným stavom (draft). Reducer aj neskoršie
 * efekty kariet menia stav VÝHRADNE cez tieto funkcie, aby každá zmena
 * vygenerovala udalosť pre klienta.
 */

import type { GameEvent } from './events.js';

import { nextInt, shuffle } from './rng.js';
import type {
  BoardToken,
  CardDef,
  CardId,
  ContentIndex,
  Fighter,
  FighterId,
  GameMap,
  GameState,
  PlayerIndex,
  PendingChoice,
  PlayerState,
  SpaceId,
} from './types.js';

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
export type EventInput = DistributiveOmit<GameEvent, 'seq'>;

export interface Ctx {
  state: GameState;
  content: ContentIndex;
  events: GameEvent[];
}

export class RuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleError';
  }
}

export function emit(ctx: Ctx, event: EventInput): void {
  ctx.state.seq += 1;
  ctx.events.push({ ...event, seq: ctx.state.seq } as GameEvent);
}

// --- prístup k stavu -------------------------------------------------------

export function getMap(ctx: Ctx): GameMap {
  const map = ctx.content.maps[ctx.state.mapId];
  if (!map) throw new RuleError(`Neznáma mapa: ${ctx.state.mapId}`);
  return map;
}

export function getFighter(state: GameState, id: FighterId): Fighter {
  const fighter = state.fighters.find((f) => f.id === id);
  if (!fighter) throw new RuleError(`Neznáma figúrka: ${id}`);
  return fighter;
}

export function getPlayer(state: GameState, index: PlayerIndex): PlayerState {
  const player = state.players[index];
  if (!player) throw new RuleError(`Neznámy hráč: ${index}`);
  return player;
}

export function cardDef(ctx: Ctx, cardId: CardId): CardDef {
  const instance = ctx.state.cards[cardId];
  if (!instance) throw new RuleError(`Neznáma karta: ${cardId}`);
  const def = ctx.content.cards[instance.defId];
  if (!def) throw new RuleError(`Neznáma definícia karty: ${instance.defId}`);
  return def;
}

export function fightersInSpace(state: GameState, spaceId: SpaceId): Fighter[] {
  return state.fighters.filter((f) => f.spaceId === spaceId);
}

export function heroOf(state: GameState, player: PlayerIndex): Fighter | undefined {
  return state.fighters.find((f) => f.owner === player && f.kind === 'hero');
}

// --- mutácie ---------------------------------------------------------------

export function moveFighter(ctx: Ctx, fighterId: FighterId, path: SpaceId[]): void {
  const fighter = getFighter(ctx.state, fighterId);
  const destination = path[path.length - 1];
  if (!destination || destination === fighter.spaceId) return;
  const from = fighter.spaceId;
  fighter.spaceId = destination;
  emit(ctx, { type: 'fighterMoved', fighterId, from, path: path.slice() });
  for (const hook of movedHooks) hook(ctx, fighter, path.slice());
}

/**
 * Reakcie na pohyb figúrky (Tomoe Gozen: odchod zo zóny). Registruje ich
 * `effects/index.ts`, priamy import by vytvoril kruh.
 */
export const movedHooks: ((ctx: Ctx, fighter: Fighter, path: SpaceId[]) => void)[] = [];

/** Poškodenie, ktoré dostane každá figúrka za jednu nedotiahnutú kartu. */
export const EXHAUSTION_DAMAGE = 2;

/**
 * Potiahnutie karty. Pri prázdnom balíčku sa odhadzovací balíček **nemieša** —
 * figúrky sú vyčerpané a každá dostane 2 poškodenia za každú kartu, ktorú
 * si hráč nemohol potiahnuť. (Core Rules, „Exhaustion")
 */
export function drawCard(ctx: Ctx, player: PlayerIndex): CardId | null {
  // Right in the Face: tento ťah sa neťahá vôbec — ani vyčerpanie nehrozí.
  if (ctx.state.turn.noDraw?.includes(player)) return null;
  const state = getPlayer(ctx.state, player);
  if (state.deck.length === 0) {
    emit(ctx, { type: 'exhausted', player, cardsMissing: 1 });
    // Poškodenie môže figúrky rovno zmiesť z dosky, preto iterujeme cez ID
    // a stav si overujeme za behu — pole `fighters` sa pod nami mení.
    const ids = ctx.state.fighters.filter((f) => f.owner === player).map((f) => f.id);
    for (const id of ids) dealDamage(ctx, id, EXHAUSTION_DAMAGE, 'exhaustion');
    return null;
  }
  const cardId = state.deck.shift();
  if (!cardId) return null;
  state.hand.push(cardId);
  emit(ctx, { type: 'cardDrawn', player, cardId, handSize: state.hand.length });
  return cardId;
}

/**
 * Reakcie na kartu, ktorú odhodil súperov efekt (And the Beautiful Bess).
 * Registruje ich `effects/index.ts` — rovnako ako `defeatHooks`.
 */
export const discardHooks: ((ctx: Ctx, player: PlayerIndex, cardId: CardId) => void)[] = [];

export function discardFromHand(
  ctx: Ctx,
  player: PlayerIndex,
  cardId: CardId,
  reason: 'played' | 'handLimit' | 'boost' | 'effect',
  byOpponent = false,
): void {
  const state = getPlayer(ctx.state, player);
  const index = state.hand.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v ruke hráča ${player}`);
  state.hand.splice(index, 1);
  state.discard.push(cardId);
  emit(ctx, { type: 'cardDiscarded', player, cardId, reason });
  if (byOpponent) for (const hook of discardHooks) hook(ctx, player, cardId);
}

/**
 * Odkiaľ poškodenie ide. Might of the Aen Elle chráni Eredina pred všetkým
 * okrem `combat` a `exhaustion`, preto to engine musí rozlišovať.
 */
export type DamageSource = 'combat' | 'effect' | 'exhaustion';

/**
 * Ochrany pred poškodením (trvalé schémy). Vráti `true` = poškodenie sa
 * neudelí. Registruje ich `effects/index.ts` — ako `defeatHooks`.
 */
/** Reakcie na utŕžené poškodenie — registruje `effects/index.ts`. */
export const damagedHooks: ((ctx: Ctx, fighter: Fighter, amount: number) => void)[] = [];

export const damageGuards: ((ctx: Ctx, fighter: Fighter, source: DamageSource) => boolean)[] = [];

export function dealDamage(ctx: Ctx, fighterId: FighterId, amount: number, source: DamageSource = 'effect'): void {
  if (amount <= 0) return;
  // Už porazená figúrka nie je na doske — ďalšie poškodenie ju nemá kde zasiahnuť.
  const fighter = ctx.state.fighters.find((f) => f.id === fighterId);
  if (!fighter) return;
  if (damageGuards.some((guard) => guard(ctx, fighter, source))) {
    emit(ctx, { type: 'damageDealt', fighterId, amount: 0, hpAfter: fighter.hp });
    return;
  }
  // Mississippi Queen: v tomto ťahu nekleslo pod 1.
  if (ctx.state.turn.floorOne?.includes(fighterId)) {
    const allowed = Math.max(0, fighter.hp - 1);
    if (amount > allowed) amount = allowed;
    if (amount <= 0) {
      emit(ctx, { type: 'damageDealt', fighterId, amount: 0, hpAfter: fighter.hp });
      return;
    }
  }
  fighter.hp = Math.max(0, fighter.hp - amount);
  emit(ctx, { type: 'damageDealt', fighterId, amount, hpAfter: fighter.hp });
  (ctx.state.turn.hurt ??= []).push(fighterId);
  // Reakcie na utŕžené poškodenie (Beowulf: +1 Rage) — aj keď figúrka padne.
  for (const hook of damagedHooks) hook(ctx, fighter, amount);
  if (fighter.hp === 0) defeatFighter(ctx, fighter);
}

/**
 * Reakcie na porazenú figúrku (Achillova schopnosť pri páde Patrokla).
 * Registruje ich `effects/index.ts` — priamy import by vytvoril kruh
 * ops → effects → ops.
 */
export const defeatHooks: ((ctx: Ctx, fighter: Fighter) => void)[] = [];

function defeatFighter(ctx: Ctx, fighter: Fighter): void {
  ctx.state.fighters = ctx.state.fighters.filter((f) => f.id !== fighter.id);
  // Pamätáme si ich — Winged Frenzy vracia porazenú Harpiu do hry.
  (ctx.state.defeated ??= []).push({ ...fighter, hp: 0 });
  emit(ctx, { type: 'fighterDefeated', fighterId: fighter.id });
  if (fighter.kind === 'hero') eliminateIfNeeded(ctx, fighter.owner);
  if (ctx.state.phase === 'ended') return;
  for (const hook of defeatHooks) hook(ctx, fighter);
}

/** Figúrky hráča na doske aj mimo nej (Vanish) — mimo dosky nie je porazená. */
function aliveFighters(state: GameState): Fighter[] {
  return [...state.fighters, ...(state.limbo ?? [])];
}

/**
 * Čo sa stane hráčovi, ktorému padol hrdina:
 *   • duel / free-for-all — vypadáva a jeho sidekicky idú okamžite z dosky preč,
 *   • 2v2 — hrá ďalej, kým mu žije aspoň jeden sidekick.
 * (Core Rules, „Free-for-all" bod 4, „Team play" bod 4)
 */
function eliminateIfNeeded(ctx: Ctx, player: PlayerIndex): void {
  const state = ctx.state;
  const seat = state.players[player];
  if (!seat || seat.eliminated) return;

  const keepsPlaying =
    state.format === 'teams' && aliveFighters(state).some((f) => f.owner === player);

  if (keepsPlaying) return;

  for (const fighter of state.fighters.filter((f) => f.owner === player)) {
    emit(ctx, { type: 'fighterDefeated', fighterId: fighter.id });
  }
  state.fighters = state.fighters.filter((f) => f.owner !== player);
  seat.eliminated = true;
  emit(ctx, { type: 'playerEliminated', player });
}

/**
 * Víťazstvo sa kontroluje po každej akcii. Vyhráva posledný tím, ktorému
 * žije hrdina; ak padnú poslední hrdinovia naraz, vyhráva tím hráča na ťahu.
 * (Core Rules, „Winning the game")
 *
 * V duele a vo free-for-all je `team === index`, takže `winner` je zároveň
 * číslo hráča.
 */
export function checkVictory(ctx: Ctx): void {
  const state = ctx.state;
  if (state.phase === 'ended') return;

  // V 2v2 môže hráč bez hrdinu ešte doťahovať sidekickom, ale tím už prehral.
  for (const seat of state.players) {
    if (!seat.eliminated && !aliveFighters(state).some((f) => f.owner === seat.index)) {
      seat.eliminated = true;
      emit(ctx, { type: 'playerEliminated', player: seat.index });
    }
  }

  const allTeams = [...new Set(state.players.map((p) => p.team))];
  const alive = allTeams.filter((team) =>
    aliveFighters(state).some((f) => f.kind === 'hero' && state.players[f.owner]?.team === team),
  );
  if (alive.length === allTeams.length) return;
  if (alive.length > 1) return; // vo free-for-all hra beží ďalej

  const winner = alive[0] ?? (state.players[state.turn.player]?.team ?? state.turn.player);
  state.phase = 'ended';
  state.winner = winner;
  // Hra môže skončiť uprostred súboja (vyčerpanie pri ťahaní za boost) —
  // karty v ňom idú do odhodu, inak by sa stratili. Karta bonusového útoku
  // v odhode už je.
  const combat = state.combat;
  if (combat) {
    const played = [combat.bonus ? null : combat.attackCard, combat.defenseCard];
    for (const cardId of played) {
      if (!cardId) continue;
      const owner = state.cards[cardId]!.owner;
      state.players[owner]!.discard.push(cardId);
      emit(ctx, { type: 'cardDiscarded', player: owner, cardId, reason: 'played' });
    }
  }
  state.combat = null;
  state.maneuver = null;
  state.pending = [];
  emit(ctx, { type: 'gameEnded', winner });
}

// --- operácie pre efekty kariet --------------------------------------------

/** Vyliečenie. Nad `maxHp` sa figúrka doliečiť nedá. */
export function healFighter(ctx: Ctx, fighterId: FighterId, amount: number): void {
  if (amount <= 0) return;
  const fighter = ctx.state.fighters.find((f) => f.id === fighterId);
  if (!fighter) return;
  const healed = Math.min(fighter.maxHp - fighter.hp, amount);
  if (healed <= 0) return;
  fighter.hp += healed;
  emit(ctx, { type: 'healed', fighterId, amount: healed, hpAfter: fighter.hp });
}

/**
 * Odhodenie náhodnej karty z ruky. Náhoda ide cez seed v stave — inak by sa
 * rozišiel replay a server s klientom.
 */
export function discardRandom(ctx: Ctx, player: PlayerIndex, byOpponent = false): CardId | null {
  const hand = getPlayer(ctx.state, player).hand;
  if (hand.length === 0) return null;
  const cardId = hand[nextInt(ctx.state, hand.length)] as CardId;
  discardFromHand(ctx, player, cardId, 'effect', byOpponent);
  return cardId;
}

/** Porazená figúrka, ktorú môže efekt vrátiť do hry. */
export function defeatedFighter(state: GameState, fighterId: FighterId): Fighter | undefined {
  return state.defeated?.find((f) => f.id === fighterId);
}

/** Vrátenie kariet z odhadzovacieho balíčka do ruky. */
export function returnFromDiscard(ctx: Ctx, player: PlayerIndex, cardIds: CardId[]): void {
  const state = getPlayer(ctx.state, player);
  const returned: CardId[] = [];
  for (const cardId of cardIds) {
    const index = state.discard.indexOf(cardId);
    if (index === -1) continue;
    state.discard.splice(index, 1);
    state.hand.push(cardId);
    returned.push(cardId);
  }
  if (returned.length > 0) emit(ctx, { type: 'cardsReturned', player, cardIds: returned });
}

/**
 * Premiestnenie figúrky bez ohľadu na cestu („place this fighter"). Cieľ musí
 * byť prázdny — v jednom priestore stojí najviac jedna figúrka.
 */
export function placeFighter(ctx: Ctx, fighterId: FighterId, spaceId: SpaceId): void {
  // Návrat z mimo dosky (Vanish): figúrka sa položí na prázdny priestor.
  // Návrat porazenej figúrky (Winged Frenzy) — s plným zdravím.
  const fallen = defeatedFighter(ctx.state, fighterId);
  if (fallen && !ctx.state.fighters.some((f) => f.id === fighterId)) {
    if (ctx.state.fighters.some((f) => f.spaceId === spaceId)) throw new RuleError('Priestor je obsadený');
    ctx.state.defeated = ctx.state.defeated!.filter((f) => f.id !== fighterId);
    const revived = { ...fallen, hp: fallen.maxHp, spaceId };
    ctx.state.fighters.push(revived);
    emit(ctx, { type: 'fighterRevived', fighterId, hp: revived.hp });
    emit(ctx, { type: 'fighterReturned', fighterId, spaceId });
    return;
  }
  const away = limboFighter(ctx.state, fighterId);
  if (away) {
    if (ctx.state.fighters.some((f) => f.spaceId === spaceId)) throw new RuleError('Priestor je obsadený');
    ctx.state.limbo = ctx.state.limbo!.filter((f) => f.id !== fighterId);
    away.spaceId = spaceId;
    delete away.returnDraw;
    ctx.state.fighters.push(away);
    emit(ctx, { type: 'fighterReturned', fighterId, spaceId });
    return;
  }
  const fighter = getFighter(ctx.state, fighterId);
  if (fighter.spaceId === spaceId) return;
  // Terrifying Roar a pripnutie: figúrka na doske tento ťah neopustí svoj priestor.
  if (ctx.state.turn.rooted || ctx.state.turn.pinned?.includes(fighterId)) return;
  if (ctx.state.fighters.some((f) => f.id !== fighterId && f.spaceId === spaceId)) {
    throw new RuleError('Priestor je obsadený');
  }
  const from = fighter.spaceId;
  fighter.spaceId = spaceId;
  emit(ctx, { type: 'fighterMoved', fighterId, from, path: [from, spaceId] });
  for (const hook of movedHooks) hook(ctx, fighter, [from, spaceId]);
}

/**
 * Zaradenie rozhodnutia do fronty. Prázdnu voľbu (bez cieľov) zahodíme —
 * a keď má pokračovanie pre prípad, že sa nevyrieši (`onSkip`), zaradí sa
 * aspoň to.
 */
export function askChoice(ctx: Ctx, choice: PendingChoice): void {
  const empty =
    (choice.kind === 'move' && choice.fighters.length === 0) ||
    (choice.kind === 'place' && choice.spaces.length === 0) ||
    (choice.kind === 'damage' && choice.targets.length === 0) ||
    (choice.kind === 'token' && choice.tokens.length === 0) ||
    (choice.kind === 'placeToken' && choice.spaces.length === 0);
  if (empty) return;
  ctx.state.pending.push(choice);
}

// --- akcie navyše, výmena miest, figúrka mimo dosky ------------------------

/**
 * „Gain 1 action." Akciu získava hráč na ťahu — mimo svojho ťahu by ju
 * nemal kedy použiť (Achilles' Heel dáva akciu útočníkovi, ktorý je na ťahu).
 */
export function gainAction(ctx: Ctx, player: PlayerIndex, amount = 1): void {
  if (ctx.state.turn.player !== player) return;
  ctx.state.turn.extraActions = (ctx.state.turn.extraActions ?? 0) + amount;
  emit(ctx, { type: 'actionGained', player, amount });
}

/** Dve figúrky si vymenia priestory (bez ohľadu na vzdialenosť). */
export function swapFighters(ctx: Ctx, a: FighterId, b: FighterId): void {
  if (ctx.state.turn.rooted) return; // Terrifying Roar
  if (ctx.state.turn.pinned?.some((id) => id === a || id === b)) return;
  const first = getFighter(ctx.state, a);
  const second = getFighter(ctx.state, b);
  const space = first.spaceId;
  first.spaceId = second.spaceId;
  second.spaceId = space;
  emit(ctx, { type: 'fightersSwapped', a, b });
  for (const hook of movedHooks) hook(ctx, first, [second.spaceId, first.spaceId]);
  for (const hook of movedHooks) hook(ctx, second, [first.spaceId, second.spaceId]);
}

/** Figúrka odíde z dosky (Vanish). Nie je porazená — vráti sa `placeFighter`. */
export function removeFromBoard(ctx: Ctx, fighterId: FighterId): void {
  const fighter = getFighter(ctx.state, fighterId);
  ctx.state.fighters = ctx.state.fighters.filter((f) => f.id !== fighterId);
  (ctx.state.limbo ??= []).push(fighter);
  emit(ctx, { type: 'fighterRemoved', fighterId });
}

/** Figúrka mimo dosky, ak tam je. */
export function limboFighter(state: GameState, fighterId: FighterId): Fighter | undefined {
  return state.limbo?.find((f) => f.id === fighterId);
}

/** Karta z ruky navrch balíčka (Coded Notes). */
export function putOnDeck(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const state = getPlayer(ctx.state, player);
  const index = state.hand.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v ruke hráča ${player}`);
  state.hand.splice(index, 1);
  state.deck.unshift(cardId);
  emit(ctx, { type: 'cardToDeck', player, cardId });
}

/**
 * Súperova karta z vrchu jeho balíčka do Vibranium Suit (Black Panther).
 * Prázdny balíček nič neuloží a nevyčerpá — nejde o ťahanie.
 */
export function storeFromDeck(ctx: Ctx, player: PlayerIndex, from: PlayerIndex, count: number): void {
  const source = getPlayer(ctx.state, from);
  const target = getPlayer(ctx.state, player);
  for (let i = 0; i < count; i++) {
    const cardId = source.deck.shift();
    if (!cardId) return;
    (target.suit ??= []).push(cardId);
    emit(ctx, { type: 'cardStored', player, cardId });
  }
}

/**
 * Odhodenie karty za boost — z ruky, alebo z Vibranium Suit. Karta zo suitu
 * patrí súperovi, preto ide do **jeho** odhodu.
 */
export function discardForBoost(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const seat = getPlayer(ctx.state, player);
  const inSuit = seat.suit?.indexOf(cardId) ?? -1;
  if (inSuit === -1) {
    discardFromHand(ctx, player, cardId, 'boost');
    return;
  }
  seat.suit!.splice(inSuit, 1);
  const owner = ctx.state.cards[cardId]!.owner;
  getPlayer(ctx.state, owner).discard.push(cardId);
  emit(ctx, { type: 'cardDiscarded', player: owner, cardId, reason: 'boost' });
}

/** Zamiešanie balíčka (Still Standing) — náhoda cez seed v stave. */
export function shuffleDeck(ctx: Ctx, player: PlayerIndex): void {
  const seat = getPlayer(ctx.state, player);
  seat.deck = shuffle(ctx.state, seat.deck);
  emit(ctx, { type: 'deckShuffled', player });
}

/** Karta z odhodu navrch balíčka (The Lady of Space and Time). */
export function discardToDeck(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const state = getPlayer(ctx.state, player);
  const index = state.discard.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v odhode hráča ${player}`);
  state.discard.splice(index, 1);
  state.deck.unshift(cardId);
  emit(ctx, { type: 'discardToDeck', player, cardId });
}

/** Karta z odhodu pod balíček (Dark Pact Doctora Strangea). */
export function discardToDeckBottom(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const state = getPlayer(ctx.state, player);
  const index = state.discard.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v odhode hráča ${player}`);
  state.discard.splice(index, 1);
  state.deck.push(cardId);
  emit(ctx, { type: 'discardToDeck', player, cardId, bottom: true });
}

/** Karta z ruky pod balíček (Method in the Madness) — ktorá, vie len majiteľ. */
export function handToDeckBottom(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const seat = getPlayer(ctx.state, player);
  const index = seat.hand.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v ruke hráča ${player}`);
  seat.hand.splice(index, 1);
  seat.deck.push(cardId);
  emit(ctx, { type: 'cardsToBottom', player, count: 1 });
}

/** Ruka (okrem `keep`) sa zamieša do balíčka (Eye of Agamotto). */
export function shuffleHandIntoDeck(ctx: Ctx, player: PlayerIndex, keep: readonly CardId[] = []): void {
  const seat = getPlayer(ctx.state, player);
  const moving = seat.hand.filter((id) => !keep.includes(id));
  seat.hand = seat.hand.filter((id) => keep.includes(id));
  seat.deck = shuffle(ctx.state, [...seat.deck, ...moving]);
  emit(ctx, { type: 'handShuffledIn', player, count: moving.length });
}

/** Karty z vrchu balíčka pod balíček v danom poradí (Calming Research). */
export function deckTopToBottom(ctx: Ctx, player: PlayerIndex, cardIds: readonly CardId[]): void {
  const seat = getPlayer(ctx.state, player);
  const moving = cardIds.filter((id) => seat.deck.includes(id));
  seat.deck = [...seat.deck.filter((id) => !moving.includes(id)), ...moving];
  emit(ctx, { type: 'cardsToBottom', player, count: moving.length });
}

/** Konkrétna karta z balíčka do ruky — ťahá sa, súper nevidí ktorá (Calming Research). */
export function drawSpecific(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const seat = getPlayer(ctx.state, player);
  const index = seat.deck.indexOf(cardId);
  if (index === -1) return;
  seat.deck.splice(index, 1);
  seat.hand.push(cardId);
  emit(ctx, { type: 'cardDrawn', player, cardId, handSize: seat.hand.length });
}

/** Hľadanie v balíčku: karta sa odhalí a ide do ruky (Searching Strike). */
export function searchDeck(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const state = getPlayer(ctx.state, player);
  const index = state.deck.indexOf(cardId);
  if (index === -1) throw new RuleError(`Karta ${cardId} nie je v balíčku hráča ${player}`);
  state.deck.splice(index, 1);
  state.hand.push(cardId);
  emit(ctx, { type: 'cardSearched', player, cardId });
}

/** Vrchné karty balíčka rovno do odhodu (Merigold's Hailstorm). Prázdny balíček nevyčerpá. */
export function millDeck(ctx: Ctx, player: PlayerIndex, count: number): void {
  const state = getPlayer(ctx.state, player);
  const cardIds = state.deck.splice(0, count);
  if (cardIds.length === 0) return;
  state.discard.push(...cardIds);
  emit(ctx, { type: 'deckMilled', player, cardIds });
}

/** Trvalá schéma hráča, ak nejaká leží. */
export function ongoingOf(state: GameState, player: PlayerIndex): CardId | null {
  return state.players[player]?.ongoing ?? null;
}

/** Trvalá schéma skončila — do odhodu. */
/** Čo sa má stať, keď trvalá schéma skončí (Polymorphy vráti pohyb) — registruje `effects/index.ts`. */
export const ongoingEndHooks: ((ctx: Ctx, player: PlayerIndex, cardId: CardId) => void)[] = [];

export function endOngoing(ctx: Ctx, player: PlayerIndex): void {
  const state = getPlayer(ctx.state, player);
  const cardId = state.ongoing;
  if (!cardId) return;
  state.ongoing = null;
  state.discard.push(cardId);
  emit(ctx, { type: 'ongoingEnded', player, cardId });
  for (const hook of ongoingEndHooks) hook(ctx, player, cardId);
}

/** Nastavenie zdravia na presnú hodnotu (Backup Plan) — nie je to poškodenie ani liečenie. */
export function setHealth(ctx: Ctx, fighterId: FighterId, hp: number): void {
  const fighter = ctx.state.fighters.find((f) => f.id === fighterId);
  if (!fighter) return;
  const target = Math.max(1, Math.min(fighter.maxHp, hp));
  if (target === fighter.hp) return;
  const diff = target - fighter.hp;
  fighter.hp = target;
  if (diff > 0) emit(ctx, { type: 'healed', fighterId, amount: diff, hpAfter: target });
  else emit(ctx, { type: 'damageDealt', fighterId, amount: -diff, hpAfter: target });
}

/** „End the turn." — zostávajúce akcie prepadnú; hráč ešte ukončí ťah. */
export function endTurnNow(ctx: Ctx): void {
  const turn = ctx.state.turn;
  // 2 = ACTIONS_PER_TURN (game.ts importuje ops.ts — priamy import by bol kruh).
  const allowed = 2 + (turn.extraActions ?? 0);
  const used = turn.actionsUsed.length;
  if (used < allowed) turn.extraActions = (turn.extraActions ?? 0) - (allowed - used);
  emit(ctx, { type: 'specialUsed', player: turn.player, heroId: '', name: 'Ťah končí' });
}

// --- žetóny (hmla) ---------------------------------------------------------

export function tokensOf(state: GameState): BoardToken[] {
  return state.tokens ?? [];
}

export function placeToken(ctx: Ctx, owner: PlayerIndex, spaceId: SpaceId): void {
  const tokens = (ctx.state.tokens ??= []);
  const id = `fog-${owner}-${tokens.filter((t) => t.owner === owner).length + 1}`;
  tokens.push({ id, kind: 'fog', owner, spaceId });
  emit(ctx, { type: 'tokenPlaced', tokenId: id, kind: 'fog', spaceId });
}

export function moveToken(ctx: Ctx, tokenId: string, spaceId: SpaceId): void {
  const token = tokensOf(ctx.state).find((t) => t.id === tokenId);
  if (!token) throw new RuleError(`Neznámy žetón: ${tokenId}`);
  if (token.spaceId === spaceId) return;
  const from = token.spaceId;
  token.spaceId = spaceId;
  emit(ctx, { type: 'tokenMoved', tokenId, from, to: spaceId });
}
